import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { UnsubscribeModal } from "./UnsubscribeModal";

// Light-grey product footer: one white newsletter card sitting on the tinted
// well, then three plain link columns and a hairline legal bar.
export function Footer() {
    const brand = useBrand();
    const [unsubOpen, setUnsubOpen] = useState(false);

    const linkCls = "text-sm text-dim hover:text-accent transition-colors duration-150";

    return (
        <footer className="mt-24 border-t border-line bg-surface-sunken" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
                {/* Newsletter band */}
                <div className="panel p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                    <div className="flex-1">
                        <h5 className="font-display text-lg font-bold tracking-[-0.02em] text-foreground" data-testid="footer-newsletter-title">
                            Newsletter
                        </h5>
                        <p className="mt-1.5 text-sm text-dim leading-relaxed">
                            Get the latest financial news delivered to your inbox.
                        </p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <button
                            onClick={() => setUnsubOpen(true)}
                            data-testid="footer-unsubscribe"
                            className="text-[13px] font-medium text-dim hover:text-neg transition-colors duration-150"
                        >
                            Unsubscribe
                        </button>
                        <Link
                            to="/register"
                            data-testid="footer-newsletter-subscribe"
                            className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-[13px] font-semibold shadow-card hover:brightness-95 hover:shadow-glow transition-[filter,box-shadow] duration-150"
                        >
                            Subscribe
                            <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 mt-14">
                    {/* Brand / contact */}
                    <div className="col-span-2">
                        <div className="flex items-center gap-2.5">
                            <span className="grid place-items-center w-9 h-9 rounded-lg bg-accent shrink-0">
                                <span className="flex items-end gap-[3px]">
                                    <span className="w-[3px] h-2 rounded-sm bg-accent-foreground/70" />
                                    <span className="w-[3px] h-3.5 rounded-sm bg-accent-foreground" />
                                    <span className="w-[3px] h-[7px] rounded-sm bg-accent-foreground/70" />
                                </span>
                            </span>
                            <span className="font-display text-[17px] font-extrabold tracking-[-0.02em] text-foreground">
                                {brand.site_name}
                            </span>
                        </div>
                        {brand.tagline && <p className="mt-4 text-sm text-dim leading-relaxed max-w-sm">{brand.tagline}</p>}
                        <p className="mt-5 font-mono text-xs text-dim" data-testid="footer-contact-email">{brand.email}</p>
                        {brand.phone && <p className="mt-1 font-mono text-xs text-dim">{brand.phone}</p>}
                    </div>

                    {/* Sections */}
                    <div>
                        <h5 className="eyebrow text-foreground mb-4">Sections</h5>
                        <ul className="space-y-2.5">
                            {CATEGORIES.map((c) => (
                                <li key={c}>
                                    <Link to={`/category/${c}`} className={linkCls}>
                                        {CATEGORY_LABELS[c]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h5 className="eyebrow text-foreground mb-4">Company</h5>
                        <ul className="space-y-2.5">
                            <li><Link to="/advertise" data-testid="footer-advertise" className={linkCls}>Advertise with us</Link></li>
                            <li><Link to="/archive" className={linkCls}>Archive</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className={linkCls}>Privacy Policy</Link></li>
                            <li><Link to="/terms" data-testid="footer-terms" className={linkCls}>Terms of Use</Link></li>
                        </ul>
                    </div>
                </div>

                <div className="rule mt-12" />
                <p className="mt-6 font-mono text-xs text-dim">
                    © {new Date().getFullYear()} {brand.site_name} · {brand.domain}
                </p>
            </div>
            <UnsubscribeModal open={unsubOpen} onOpenChange={setUnsubOpen} />
        </footer>
    );
}
